import { Fragment } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { ApiError, describeError, type Session } from '../api/client';
import {
  addCommonplaceBook,
  addCommonplaceEntry,
  editCommonplaceEntry,
  commonplaceBooks,
  commonplaceEntries,
} from '../api/collections';
import { displayDate } from '../api/adapter';
import type { WireCommonplaceBook, WireCommonplaceEntry } from '../api/wire';

import { EmptyState } from '../components/feedback/EmptyState.jsx';
import { Pressable } from '../components/a11y/Pressable';
import { SegmentedNav } from '../components/a11y/SegmentedNav';
import { SectionHeading } from '../components/composed/SectionHeading';

type EntryType = 'quote' | 'list' | 'note';

/** The three entry types, each a bordered word rather than a colour.
 *
 *  The service accepts exactly `quote | list | note` and rejects anything else
 *  with `{field: 'entry_type', allowed: [...]}`, so this list is the contract
 *  and not a choice made here. */
const TYPES: Record<EntryType, { word: string; colour: string }> = {
  quote: { word: 'QUOTE', colour: 'var(--bujo-label)' },
  list: { word: 'LIST', colour: 'var(--bujo-accent)' },
  note: { word: 'NOTE', colour: 'var(--bujo-token-context)' },
};

/** Commonplace books: an index of books, and one book's entries.
 *
 *  The last of U11's three surfaces. The Index has listed this as
 *  `not built yet` since the collection shipped server-side.
 *
 *  Entries never truncate — a quote you kept is the whole point of keeping it —
 *  so the book index is the only thing here that pages.
 */
export function Commonplace({
  session,
  onBack,
}: {
  session: Session;
  onBack: () => void;
}) {
  const [books, setBooks] = useState<WireCommonplaceBook[] | null>(null);
  const [openBook, setOpenBook] = useState<string | null>(null);
  const [entries, setEntries] = useState<WireCommonplaceEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Which type the editor is composing, or null for the index. The frame's CTA
  // is `+ quote · list · note`, so the type is chosen on the way in rather than
  // switched afterwards — the segmented control still shows and still works.
  const [composing, setComposing] = useState<EntryType | null>(null);
  const [addingBook, setAddingBook] = useState(false);
  // Which entry is open for editing, and its working copy — the Gratitude Log's
  // idiom: the row itself becomes the field, rather than a sheet or a screen.
  const [editing, setEditing] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState({ body: '', title: '' });
  const [bookDraft, setBookDraft] = useState({ title: '', description: '' });
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await commonplaceBooks(session);
        if (cancelled) return;
        setBooks(list);
        setOpenBook((current) => current ?? list[0]?.id ?? null);
      } catch (caught) {
        if (!cancelled) setError(scopeAware(caught));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session]);

  /** Save a change to a kept entry.
   *
   *  `source` is not offered: `UpdateCommonplaceEntryRequest` does not accept it,
   *  so a citation is fixed once kept. A 412 means it changed underneath — the
   *  honest response is to reload and let the current text be seen, not to
   *  overwrite it.
   */
  async function saveEdit(entry: WireCommonplaceEntry) {
    const body = editDraft.body.trim();
    if (!body || !openBook) return;
    try {
      await editCommonplaceEntry(
        session,
        openBook,
        entry,
        entry.entry_type === 'list'
          ? { body, title: editDraft.title.trim() || entry.title }
          : { body },
      );
      setEditing(null);
      await loadEntries(openBook);
    } catch (caught) {
      setError(scopeAware(caught));
      if (caught instanceof ApiError && caught.isRevisionConflict) {
        setEditing(null);
        await loadEntries(openBook);
      }
    }
  }

  async function loadEntries(bookId: string) {
    try {
      setEntries(await commonplaceEntries(session, bookId));
      setError(null);
    } catch (caught) {
      setError(scopeAware(caught));
    }
  }

  useEffect(() => {
    if (!openBook) return;
    let cancelled = false;
    setEntries(null);
    void (async () => {
      const found = await commonplaceEntries(session, openBook).catch((caught) => {
        if (!cancelled) setError(scopeAware(caught));
        return null;
      });
      if (!cancelled && found) setEntries(found);
    })();
    return () => {
      cancelled = true;
    };
  }, [session, openBook]);

  const book = books?.find((b) => b.id === openBook) ?? null;
  // The service keeps `source` free-form — "where it came from, in the keeper's
  // own words" — and `source_type` is provenance (`typed`/`llm`/`import`/`api`),
  // not bibliography. So the frame's AUTHOR / WORK / PAGE rows are an input aid
  // that composes the one line the list then renders back:
  // `Kovach · The Art of the Practice p.61`.
  const composedSource = composeSource(draft);
  const words = draft.body.trim() ? draft.body.trim().split(/\s+/).length : 0;

  const editorPage = book ? (
    <form
      onSubmit={async (event) => {
        event.preventDefault();
        if (!draft.body.trim() || !openBook) return;
        try {
          await addCommonplaceEntry(session, openBook, {
            entry_type: draft.type,
            // The service requires a title. The frame only shows one for
            // a list, where it is the heading, so the other two derive
            // it from what was written rather than asking twice.
            title:
      draft.type === 'list'
        ? draft.title.trim() || draft.body.trim().split('\n')[0]!.slice(0, 60)
        : draft.body.trim().slice(0, 60),
            body: draft.body.trim(),
            source: composedSource,
            tags: draft.tags
      .split(/[\s,]+/)
      .map((tag) => tag.trim())
      .filter(Boolean),
          });
          setComposing(null);
          setDraft(EMPTY_DRAFT);
          await loadEntries(openBook);
        } catch (caught) {
          setError(scopeAware(caught));
        }
      }}
      style={form}
    >
      <div style={editorHead}>
        <span style={{ ...eyebrow, paddingBottom: 0, color: 'var(--bujo-label)' }}>
          NEW ENTRY
        </span>
        <button
          type="submit"
          disabled={!draft.body.trim()}
          style={saveAction(Boolean(draft.body.trim()))}
        >
          SAVE
        </button>
      </div>
      <div style={meta}>
        {book.title.toUpperCase()} · {displayDate(new Date().toISOString().slice(0, 10))}
      </div>

      <SegmentedNav
        label="Entry type"
        variant="boxed"
        active={draft.type}
        onSelect={(type) => setDraft({ ...draft, type })}
        options={[
          { value: 'quote' as EntryType, label: 'QUOTE' },
          { value: 'list' as EntryType, label: 'LIST' },
          { value: 'note' as EntryType, label: 'NOTE' },
        ]}
      />

      {draft.type === 'list' ? (
        <Field label="HEADING">
          <input
            value={draft.title}
            onInput={(e) => setDraft({ ...draft, title: (e.target as HTMLInputElement).value })}
            placeholder="Three questions before a spike"
            style={input}
          />
        </Field>
      ) : null}

      <Field label="TEXT">
        <textarea
          value={draft.body}
          onInput={(e) => setDraft({ ...draft, body: (e.target as HTMLTextAreaElement).value })}
          placeholder={draft.type === 'list' ? 'one item per line' : 'what you want to keep'}
          rows={5}
          style={{
            ...input,
            resize: 'none',
            // A quote reads as a quote while it is being written, which
            // is also how it will be rendered back.
            fontStyle: draft.type === 'quote' ? 'italic' : 'normal',
          }}
        />
        {draft.type === 'quote' ? (
          <span style={{ ...meta, color: 'var(--bujo-text-faint)' }}>
            quotation marks added on save
          </span>
        ) : null}
      </Field>

      <div style={{ ...eyebrow, paddingBottom: 0, color: 'var(--bujo-label)' }}>SOURCE</div>
      <Field label="AUTHOR" layout="row">
        <input
          value={draft.author}
          onInput={(e) => setDraft({ ...draft, author: (e.target as HTMLInputElement).value })}
          placeholder="Kovach"
          style={bare}
        />
      </Field>
      <Field label="WORK" layout="row">
        <input
          value={draft.work}
          onInput={(e) => setDraft({ ...draft, work: (e.target as HTMLInputElement).value })}
          placeholder="The Art of the Practice"
          style={bare}
        />
      </Field>
      <Field label="PAGE" layout="row">
        <input
          value={draft.page}
          onInput={(e) => setDraft({ ...draft, page: (e.target as HTMLInputElement).value })}
          placeholder="61"
          inputMode="numeric"
          style={bare}
        />
      </Field>
      <Field label="TAGS" layout="row">
        <input
          value={draft.tags}
          onInput={(e) => setDraft({ ...draft, tags: (e.target as HTMLInputElement).value })}
          placeholder="/Personal"
          autocapitalize="none"
          spellcheck={false}
          style={bare}
        />
      </Field>

      <div style={editorFoot}>
        <span style={meta}>
          {draft.type.toUpperCase()} · {words} WORD{words === 1 ? '' : 'S'}
        </span>
        <span style={meta}>{composedSource ? 'SOURCE COMPLETE' : 'NO SOURCE'}</span>
      </div>
      <button type="button" onClick={() => setComposing(null)} style={quiet}>
        cancel
      </button>
    </form>
  ) : null;

  // The editor is its own page, as the frame draws it — a considered act with a
  // citation to fill in, not a form tacked onto the end of a list.
  if (composing && editorPage) {
    return <main style={page}>{editorPage}</main>;
  }

  return (
    <main style={page}>
      <header style={header}>
        <div style={eyebrow}>BUJO / COMMONPLACE</div>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--bujo-space-3)' }}>
          <h1 style={h1}>BOOK INDEX</h1>
          {books ? (
            <span style={meta}>
              {books.length} BOOK{books.length === 1 ? '' : 'S'}
            </span>
          ) : null}
        </div>
      </header>

      {error ? <p style={errorLine}>! {error}</p> : null}

      <section>
        {books === null ? null : books.length === 0 && !addingBook ? (
          <EmptyState line="No books yet." detail="Start one below." />
        ) : (
          books.map((b) => (
            <Pressable
              key={b.id}
              label={`${b.title}${b.id === openBook ? ', open' : ''}`}
              onPress={() => setOpenBook(b.id)}
            >
              {/* Selection is an offset ground plus a hard left edge, never
                  colour alone. */}
              <div style={b.id === openBook ? { ...bookRow, ...selected } : bookRow}>
                <span style={bookTitle}>{b.title}</span>
                <span style={meta}>{b.description || 'no description'}</span>
              </div>
            </Pressable>
          ))
        )}

        {addingBook ? (
          <form
            onSubmit={async (event) => {
              event.preventDefault();
              const title = bookDraft.title.trim();
              if (!title) return;
              try {
                await addCommonplaceBook(session, title, bookDraft.description.trim());
                setAddingBook(false);
                setBookDraft({ title: '', description: '' });
                const list = await commonplaceBooks(session);
                setBooks(list);
                // Open what was just made: the reason for making it is to put
                // something in it.
                setOpenBook(list.find((b) => b.title === title)?.id ?? openBook);
              } catch (caught) {
                setError(scopeAware(caught));
              }
            }}
            style={form}
          >
            <Field label="TITLE">
              <input
                value={bookDraft.title}
                onInput={(e) => setBookDraft({ ...bookDraft, title: (e.target as HTMLInputElement).value })}
                placeholder="Working notes"
                style={input}
              />
            </Field>
            <Field label="DESCRIPTION">
              <input
                value={bookDraft.description}
                onInput={(e) =>
                  setBookDraft({ ...bookDraft, description: (e.target as HTMLInputElement).value })
                }
                placeholder="things worth keeping"
                style={input}
              />
            </Field>
            <div style={{ display: 'flex', gap: 'var(--bujo-space-4)', alignItems: 'center' }}>
              <button type="submit" disabled={!bookDraft.title.trim()} style={primary(Boolean(bookDraft.title.trim()))}>
                START THE BOOK
              </button>
              <button type="button" onClick={() => setAddingBook(false)} style={quiet}>
                cancel
              </button>
            </div>
          </form>
        ) : (
          <button type="button" onClick={() => setAddingBook(true)} style={addRow}>
            + new book
          </button>
        )}
      </section>

      {book ? (
        <section>
          <SectionHeading active marker="OPEN" right="NEWEST FIRST">
            {book.title.toUpperCase()}
          </SectionHeading>
          {entries === null ? null : entries.length === 0 ? (
            <EmptyState line="Nothing kept in this book yet." />
          ) : (
            entries.map((entry) => {
              const type = TYPES[(entry.entry_type as EntryType) in TYPES ? (entry.entry_type as EntryType) : 'note'];
              const isQuote = entry.entry_type === 'quote';
              const isList = entry.entry_type === 'list';
              const isEditing = editing === entry.id;
              return (
                <article key={entry.id} style={entryRow}>
                  <div style={{ display: 'flex', gap: 'var(--bujo-space-3)', alignItems: 'baseline' }}>
                    <span style={{ ...typeWord, color: type.colour }}>{type.word}</span>
                    <span style={meta}>{displayDate(entry.created_at.slice(0, 10))}</span>
                  </div>
                  {isEditing ? (
                    <form
                      onSubmit={(event) => {
                        event.preventDefault();
                        void saveEdit(entry);
                      }}
                      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bujo-space-2)' }}
                    >
                      {isList ? (
                        <Field label="HEADING">
                          <input
                            value={editDraft.title}
                            onInput={(e) =>
                              setEditDraft({ ...editDraft, title: (e.target as HTMLInputElement).value })
                            }
                            style={input}
                          />
                        </Field>
                      ) : null}
                      <Field label="TEXT">
                        <textarea
                          value={editDraft.body}
                          onInput={(e) =>
                            setEditDraft({ ...editDraft, body: (e.target as HTMLTextAreaElement).value })
                          }
                          rows={isList ? 6 : 4}
                          autofocus
                          style={{
                            ...input,
                            resize: 'none',
                            fontStyle: isQuote ? 'italic' : 'normal',
                          }}
                        />
                      </Field>
                      {entry.source ? (
                        <span style={{ ...meta, color: 'var(--bujo-text-faint)' }}>
                          {entry.source} · the source is fixed once kept
                        </span>
                      ) : null}
                      <div style={{ display: 'flex', gap: 'var(--bujo-space-4)', alignItems: 'center' }}>
                        <button
                          type="submit"
                          disabled={!editDraft.body.trim()}
                          style={saveAction(Boolean(editDraft.body.trim()))}
                        >
                          SAVE
                        </button>
                        <button type="button" onClick={() => setEditing(null)} style={quiet}>
                          cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    <Pressable
                      label={`${type.word}: ${entry.body} — tap to edit`}
                      onPress={() => {
                        setEditing(entry.id);
                        setEditDraft({ body: entry.body, title: entry.title });
                      }}
                    >
                      {/* Only a list carries a visible heading in the frame; a
                          quote is its own words and a note is prose, and printing
                          a derived title above either just says it twice. */}
                      {isList && entry.title ? <div style={entryTitle}>{entry.title}</div> : null}
                      <div
                        style={{
                          // A list keeps mono alignment because the alignment is
                          // the content; a quote is italic prose; a note is plain
                          // prose.
                          font: isList
                            ? 'var(--bujo-fw-regular) var(--bujo-fs-event)/1.7 var(--bujo-font-mono)'
                            : 'var(--bujo-fw-regular) 14px/1.6 var(--bujo-font-sans)',
                          fontStyle: isQuote ? 'italic' : 'normal',
                          color: 'var(--bujo-text-primary)',
                          // Entries never truncate, and a list's line breaks are
                          // part of it.
                          whiteSpace: 'pre-line',
                        }}
                      >
                        {isQuote ? `“${entry.body}”` : entry.body}
                      </div>
                      {entry.source ? <div style={meta}>{entry.source}</div> : null}
                    </Pressable>
                  )}
                </article>
              );
            })
          )}

          <div style={footnote}>
            Entry type is a bordered word — <b style={{ color: TYPES.quote.colour }}>QUOTE</b> keeps
            its source line, <b style={{ color: TYPES.list.colour }}>LIST</b> keeps mono alignment,{' '}
            <b style={{ color: TYPES.note.colour }}>NOTE</b> is sans prose. Entries never truncate;
            the book index is the only place that pages.
          </div>
          <div style={ctaRow}>
            <span style={{ color: 'var(--bujo-accent)', font: 'var(--bujo-fw-bold) var(--bujo-fs-entry)/1 var(--bujo-font-mono)' }}>
              +
            </span>
            {(['quote', 'list', 'note'] as EntryType[]).map((type, index) => (
              // The key belongs on the outermost node of each iteration, not on
              // a child of an unkeyed fragment.
              <Fragment key={type}>
                {index ? <span style={{ ...meta, color: 'var(--bujo-text-faint)' }}>·</span> : null}
                <button
                  type="button"
                  onClick={() => {
                    setDraft({ ...EMPTY_DRAFT, type });
                    setComposing(type);
                  }}
                  style={ctaWord}
                >
                  {type}
                </button>
              </Fragment>
            ))}
          </div>

        </section>
      ) : null}

      <button type="button" onClick={onBack} style={back}>
        ◀ BACK TO INDEX
      </button>
    </main>
  );
}

/** A 403 here means the token lacks `commonplace:read`/`commonplace:write`,
 *  which are separate scopes from the rest of the journal — worth saying, since
 *  the generic message sends you looking for the wrong problem. */
function scopeAware(caught: unknown): string {
  if (caught instanceof ApiError && caught.status === 403) {
    return 'This token cannot read commonplace books — it needs the commonplace:read and commonplace:write scopes.';
  }
  return describeError(caught);
}

interface Draft {
  type: EntryType;
  /** A list's heading. Derived from the body for the other two types. */
  title: string;
  body: string;
  author: string;
  work: string;
  page: string;
  /** Space or comma separated, in the product's own token vocabulary. */
  tags: string;
}

const EMPTY_DRAFT: Draft = {
  type: 'quote',
  title: '',
  body: '',
  author: '',
  work: '',
  page: '',
  tags: '',
};

/** The frame's three source rows as the one line the service stores.
 *
 *  `Kovach · The Art of the Practice p.61` — which is exactly what the entry
 *  list renders back, so the round trip is faithful in appearance even though it
 *  is lossy in structure. Worth knowing before an edit screen is built: parsing
 *  this back into three fields would be a guess, so an editor should either keep
 *  the raw line or the service should grow real fields.
 */
function composeSource(draft: { author: string; work: string; page: string }): string {
  const named = [draft.author.trim(), draft.work.trim()].filter(Boolean).join(' · ');
  const page = draft.page.trim();
  if (!named) return page ? `p.${page}` : '';
  return page ? `${named} p.${page}` : named;
}

/** A labelled field.
 *
 *  Two shapes, both from the frame: `TEXT` sits above its area, while the source
 *  block is a column of horizontal rows — label left, value right, a hairline
 *  under each. The rows are what make the block read as a citation rather than
 *  four more form fields.
 */
function Field({
  label,
  layout = 'stacked',
  children,
}: {
  label: string;
  layout?: 'stacked' | 'row';
  children: unknown;
}) {
  const name = (
    <span
      style={{
        font: 'var(--bujo-fw-regular) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
        letterSpacing: 'var(--bujo-ls-label)',
        color: 'var(--bujo-text-meta)',
        flex: layout === 'row' ? '0 0 5.5rem' : undefined,
      }}
    >
      {label}
    </span>
  );
  return layout === 'row' ? (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--bujo-space-3)',
        minHeight: 'var(--bujo-row-min)',
        borderBottom: '1px solid var(--bujo-rule-hairline)',
      }}
    >
      {name}
      {children as never}
    </label>
  ) : (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bujo-space-1)' }}>
      {name}
      {children as never}
    </label>
  );
}

/** Underline, not a box — the frame draws a citation, not a form. */
const bare = {
  flex: 1,
  minWidth: 0,
  background: 'transparent',
  border: 0,
  outline: 'none',
  padding: 0,
  color: 'var(--bujo-text-primary)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-entry)/1 var(--bujo-font-mono)',
};

const ctaRow = {
  display: 'flex',
  alignItems: 'center' as const,
  gap: 'var(--bujo-space-3)',
  minHeight: 'var(--bujo-row-min)',
  borderTop: '1px solid var(--bujo-rule-hairline)',
  paddingTop: 'var(--bujo-space-2)',
};
const ctaWord = {
  minHeight: 'var(--bujo-row-min)',
  padding: 0,
  background: 'none',
  border: 0,
  cursor: 'pointer',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-entry)/1 var(--bujo-font-mono)',
  color: 'var(--bujo-text-meta)',
};
const form = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 'var(--bujo-space-3)',
  paddingTop: 'var(--bujo-space-4)',
};
const editorHead = {
  display: 'flex',
  alignItems: 'center' as const,
  justifyContent: 'space-between' as const,
};
const editorFoot = {
  display: 'flex',
  alignItems: 'baseline' as const,
  justifyContent: 'space-between' as const,
  paddingTop: 'var(--bujo-space-2)',
  borderTop: '1px solid var(--bujo-rule-hairline)',
};
const saveAction = (ready: boolean) => ({
  minHeight: 'var(--bujo-row-min)',
  padding: '0 var(--bujo-space-2)',
  background: 'none',
  border: 0,
  font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-label)',
  color: ready ? 'var(--bujo-accent)' : 'var(--bujo-text-faint)',
  cursor: ready ? 'pointer' : 'default',
});

const page = { padding: 'var(--bujo-gutter)', maxWidth: 720, margin: '0 auto' };
const header = {
  paddingBottom: 'var(--bujo-space-2)',
  marginBottom: 'var(--bujo-space-4)',
  borderBottom: '1px solid var(--bujo-rule-heavy)',
};
const eyebrow = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-eyebrow)',
  color: 'var(--bujo-text-meta)',
  paddingBottom: 'var(--bujo-space-2)',
};
const h1 = {
  margin: 0,
  font: 'var(--bujo-fw-medium) var(--bujo-fs-display)/1.1 var(--bujo-font-mono)',
  color: 'var(--bujo-accent)',
};
const bookRow = {
  display: 'flex',
  flexDirection: 'column' as const,
  justifyContent: 'center' as const,
  gap: 2,
  minHeight: 'var(--bujo-row-min)',
  borderBottom: '1px solid var(--bujo-rule-hairline)',
};
const selected = {
  background: 'var(--bujo-select-ground)',
  boxShadow: 'inset var(--bujo-select-edge-width) 0 0 var(--bujo-select-edge)',
  paddingLeft: 'var(--bujo-space-2)',
  marginLeft: 'calc(var(--bujo-space-2) * -1)',
};
const bookTitle = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-event)/var(--bujo-lh-row) var(--bujo-font-mono)',
  color: 'var(--bujo-text-primary)',
};
const meta = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1.5 var(--bujo-font-mono)',
  color: 'var(--bujo-text-meta)',
};
const entryRow = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 'var(--bujo-space-2)',
  padding: 'var(--bujo-space-3) 0',
  borderBottom: '1px solid var(--bujo-rule-hairline)',
};
const typeWord = {
  flex: 'none' as const,
  font: 'var(--bujo-fw-bold) var(--bujo-fs-micro)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-badge)',
  border: '1px solid var(--bujo-rule-hairline)',
  padding: '4px 5px',
};
const entryTitle = {
  font: 'var(--bujo-fw-bold) var(--bujo-fs-meta)/1.4 var(--bujo-font-mono)',
  color: 'var(--bujo-text-dim)',
};
const input = {
  minHeight: 'var(--bujo-row-min)',
  background: 'var(--bujo-surface-sheet)',
  border: '1px solid var(--bujo-rule-hairline)',
  color: 'var(--bujo-text-primary)',
  borderRadius: 0,
  padding: 'var(--bujo-space-2)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-event)/1.5 var(--bujo-font-mono)',
};
const primary = (enabled: boolean) => ({
  minHeight: 'var(--bujo-tap-min)',
  background: 'none',
  border: `1px solid ${enabled ? 'var(--bujo-accent)' : 'var(--bujo-rule-hairline)'}`,
  color: enabled ? 'var(--bujo-accent)' : 'var(--bujo-text-faint)',
  font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-label)',
  cursor: enabled ? 'pointer' : 'default',
});
const quiet = {
  minHeight: 'var(--bujo-row-min)',
  // Left, with everything else. A full-width button centres its label, which
  // read as a stray word floating under the form.
  textAlign: 'left' as const,
  alignSelf: 'flex-start' as const,
  background: 'none',
  border: 0,
  padding: 0,
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1 var(--bujo-font-mono)',
  color: 'var(--bujo-text-meta)',
  cursor: 'pointer',
};
const addRow = {
  minHeight: 'var(--bujo-row-min)',
  textAlign: 'left' as const,
  width: '100%',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-event)/1 var(--bujo-font-mono)',
  color: 'var(--bujo-accent)',
  cursor: 'pointer',
};
const footnote = {
  paddingTop: 'var(--bujo-space-4)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-micro)/1.7 var(--bujo-font-mono)',
  color: 'var(--bujo-text-meta)',
};
const back = {
  marginTop: 'var(--bujo-space-4)',
  minHeight: 'var(--bujo-row-min)',
  font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-badge)',
  color: 'var(--bujo-accent)',
  cursor: 'pointer',
};
const errorLine = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1.4 var(--bujo-font-mono)',
  color: 'var(--bujo-state-error)',
};
